import React, { useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Card, Form, Input, Button, message } from "antd";
import { UserOutlined, MailOutlined, PhoneOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { getAuth } from "firebase/auth";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:4000/api";

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: var(--color-bg-primary, #f5f5f5);
`;

const StyledCard = styled(Card)`
  max-width: 440px;
  width: 100%;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  .ant-card-body {
    padding: 1.5rem;
  }
`;

const CATEGORY_LABELS = {
  Plumbing: "Plumber",
  Electrical: "Electrician",
  HVAC: "HVAC / Heating & Cooling",
  General: "General contractor / handyman",
};

export default function SetupVendorPage() {
  const { propertyId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const category = searchParams.get("category") || "General";
  const ticketId = searchParams.get("ticketId") || "";
  const categoryLabel = CATEGORY_LABELS[category] || category;

  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const handleSubmit = async (values) => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const user = getAuth().currentUser;
      const token = user ? await user.getIdToken() : null;
      if (!token) {
        message.error("Please sign in to add a vendor.");
        setLoading(false);
        return;
      }

      const res = await fetch(
        `${API_BASE_URL}/properties/${propertyId}/setup-vendor`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            category,
            name: values.name?.trim(),
            email: values.email?.trim(),
            phone: values.phone?.trim(),
            ...(ticketId && { ticketId }),
          }),
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to save vendor");

      message.success(
        "Vendor saved. Leo will draft a response for your ticket."
      );
      if (data.quickActionUrl) {
        window.location.href = data.quickActionUrl;
      } else {
        navigate("/repairs");
      }
    } catch (err) {
      message.error(err.message || "Failed to save vendor");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <StyledCard
        title={`Add your preferred ${categoryLabel}`}
        subTitle={
          ticketId
            ? "Leo will immediately draft a response for your open ticket."
            : undefined
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Vendor name"
            rules={[{ required: true, message: "Enter the vendor's name" }]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="e.g. Joe's Plumbing"
            />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Enter a valid email" },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              type="email"
              placeholder="vendor@example.com"
            />
          </Form.Item>
          <Form.Item name="phone" label="Phone (optional)">
            <Input prefix={<PhoneOutlined />} placeholder="(555) 123-4567" />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              size="large"
            >
              Save & draft response
            </Button>
          </Form.Item>
        </Form>
      </StyledCard>
    </Page>
  );
}
